import React, { useEffect, useMemo, useState } from 'react';

import queryString from 'query-string';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { DefaultLandingMessage } from 'components';
import { LINKS } from 'components/constants';
import { ConfirmationDialog } from 'components/Dialogs';
import { DEFAULT_TABLE_PARAMS } from 'components/TableComponent/constants';
import { useAuthState } from 'contexts/AuthContext';
import { useBusyProgressActions } from 'contexts/BusyProgressContext';
import { useServerErrorActions } from 'contexts/ServerErrorContext';
import PageWithHeader from 'layouts/PageWithHeader/PageWithHeader';
import { categoriesModel, productModel } from 'models';
import { ROUTES } from 'routes/constants';
import { ProductListItem } from 'types';
import { ActionType, OrderByParameter } from 'types/enums';
import { CategoryForSelect, TableParams } from 'types/other';
import { getImageThumbUrl } from 'utils';
import TEST_IDS from 'constants/testIds';
import { useShoppingProviderStatus } from 'contexts/ShoppingProviderContext';
import DisconnectInProgressBlocker from 'components/DisconnectStatus/DisconnectInProgressBlocker';

import { BigAvatar, ProductsTable } from './style';

import noPhoto from 'assets/img/nophoto.jpg';

const ManageProducts = () => {
  const { shouldBlock } = useShoppingProviderStatus();

  const history = useHistory();
  const { t } = useTranslation();
  const { withPageProgressHandler } = useBusyProgressActions();
  const { handleServerError } = useServerErrorActions();
  const { allowAddProduct } = useAuthState().currentUser.attributes;

  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [categories, setCategories] = useState<CategoryForSelect[]>([]);
  const [productToDelete, setProductToDelete] = useState<ProductListItem>();
  const [tableParams, setTableParams] = useState<TableParams>({ ...DEFAULT_TABLE_PARAMS });
  const [paginationItemsCount, setPaginationItemsCount] = useState(0);

  useEffect(() => {
    if (history.location.search) history.push(history.location.pathname, { search: '' });
    else {
      getCategoriesForSelect();
    }
  }, []);

  useEffect(() => {
    if (!shouldBlock) getData();
  }, [tableParams, shouldBlock]);

  const getData = withPageProgressHandler(() => {
    const { filterId, search, ...rest } = tableParams;

    return productModel
      .getProducts({
        ...rest,
        categoryId: filterId,
        identifier: search,
        name: search,
      })
      .then(data => {
        setProducts(data.products);
        setPaginationItemsCount(data.productsCount);
      })
      .catch(handleServerError);
  });

  const getCategoriesForSelect = () => {
    categoriesModel
      .getCategoriesForSelect()
      .then(({ categories }) => setCategories(categories))
      .catch(handleServerError);
  };

  const handleDelete = withPageProgressHandler(({ id }) => {
    setProductToDelete(undefined);
    return productModel.deleteProduct(id).then(getData).catch(handleServerError);
  });

  const handleRedirect = (product: ProductListItem) => history.push(`${ROUTES.editProductNamespace}${product.id}`);

  const tableData = useMemo(
    () => ({
      headers: [
        { title: '' },
        { title: t('components.tableComponent.headers.email'), sortKey: OrderByParameter.NAME },
        { title: t('components.tableComponent.headers.identifier'), sortKey: OrderByParameter.IDENTIFIER },
        { title: t('components.tableComponent.headers.category'), sortKey: OrderByParameter.CATEGORY_NAME },
        { title: t('components.tableComponent.headers.updated'), sortKey: OrderByParameter.UPDATED_AT },
      ],
      rows: products.map(product => ({
        id: product.id,
        cells: [
          <BigAvatar variant="square" src={product.pictures[0] ? getImageThumbUrl(product.pictures[0]) : noPhoto} />,
          product.name,
          product.identifier,
          product.categoryName,
          product.updatedAt,
        ],
        onClick: () => handleRedirect(product),
        actions: [
          {
            type: ActionType.EDIT,
            onClick: () => handleRedirect(product),
            testId: TEST_IDS.manageProducts.list.buttons.edit,
          },
          {
            type: ActionType.DELETE,
            onClick: () => setProductToDelete(product),
            testId: TEST_IDS.manageProducts.list.buttons.delete,
          },
        ],
      })),
    }),
    [products],
  );

  if (shouldBlock) return <DisconnectInProgressBlocker />;

  return (
    <PageWithHeader
      tabTitle={t('pages.products.manageProducts.title')}
      headerText={`${t('pages.products.manageProducts.title')}: ${paginationItemsCount}`}
      button={
        allowAddProduct
          ? { text: t('pages.products.manageProducts.addProduct'), onClick: () => history.push(ROUTES.newProduct) }
          : undefined
      }
    >
      <ProductsTable
        data={tableData}
        count={paginationItemsCount}
        filterItems={categories.map(category => ({ id: category.id, value: category.nameWithPrefix }))}
        filterLabel="Category"
        initialFilter={Number(queryString.parse(history.location.search).categoryId) || undefined}
        tableParams={tableParams}
        onParamsChange={params => setTableParams(prev => ({ ...prev, ...params }))}
        enableSearch
      />
      {paginationItemsCount === 0 && (
        <DefaultLandingMessage
          title={t('pages.products.manageProducts.defaultLandingTitle')}
          body={t('pages.products.manageProducts.defaultLandingMessage')}
          bodyLink={LINKS.productsKnowledgeBase}
        />
      )}
      {productToDelete && (
        <ConfirmationDialog
          open
          message={t('pages.products.manageProducts.confirmDelete')}
          handleYes={() => handleDelete(productToDelete)}
          handleNo={() => setProductToDelete(undefined)}
        />
      )}
    </PageWithHeader>
  );
};

export default ManageProducts;
